/**
 * GF-Startseite (Joerg 2026-07-07).
 *
 * Feste Widget-Anordnung fuer die Geschaeftsfuehrung — kein Widget-Editor,
 * keine Tabs. Vier Widgets in festem Layout: Mannschaft, Laufend jetzt,
 * Offen gesamt oben; Team-Karte darunter volle Breite. Datenquelle
 * identisch zur GF-Widget-Grid-Seite: /portal/dashboard/fuehrung (15s-Poll).
 *
 * Sichtbarkeits-Guard: 403 vom Aggregat-Endpoint → Redirect auf /
 * (dann greift die alte HomePage-Logik fuer nicht-GF-User).
 *
 * Die persoenliche Widget-Grid-Seite mit Tabs (Heute · Mein Tag · Puls …)
 * bleibt via /uebersicht/… weiter erreichbar — hier wird sie NICHT ersetzt,
 * nur der Sidebar-„Startseite"-Klick fuer GF/Admin fuehrt hierher.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, RefreshCw } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';
import {
  renderMannschaft, renderLaufendJetzt, renderOffenSummary, TagesKarteWidget,
  type DashboardData,
} from './uebersicht/UebersichtFuehrung';

const POLL_MS = 15_000;

function todayLabel(): string {
  return new Date().toLocaleDateString('de-DE', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  });
}

export default function HomeGF() {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    api
      .get<{ success: boolean; data: DashboardData }>('/portal/dashboard/fuehrung')
      .then((res) => {
        if (res.success) {
          setData(res.data);
          setError(null);
        } else {
          setError('Startseite konnte nicht geladen werden');
        }
      })
      .catch((err: unknown) => {
        const status = (err as { status?: number }).status;
        if (status === 403) {
          // Backend sagt: nicht GF. Zurueck auf die Standard-Startseite.
          navigate('/', { replace: true });
          return;
        }
        setError('Fehler beim Laden');
      })
      .finally(() => { if (!silent) setLoading(false); });
  }, [navigate]);

  useEffect(() => {
    load();
    const id = window.setInterval(() => load(true), POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">{error}</div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Heute</h1>
          <p className="text-sm text-muted-foreground mt-1 capitalize">{todayLabel()}</p>
        </div>
        <button
          type="button"
          onClick={() => load()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-default text-foreground hover:bg-muted text-sm min-h-[40px] disabled:opacity-50"
          title="Aktualisieren"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Aktualisieren
        </button>
      </header>

      {/* Obere Reihe: 3 Widgets nebeneinander (mobile: gestapelt) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-default bg-card/60 overflow-hidden">
          {renderMannschaft(data)}
        </div>
        <div className="rounded-xl border border-default bg-card/60 overflow-hidden">
          {renderLaufendJetzt(data)}
        </div>
        <div className="rounded-xl border border-default bg-card/60 overflow-hidden">
          {renderOffenSummary(data)}
        </div>
      </div>

      {/* Team-Karte volle Breite — feste Hoehe damit Leaflet rendert */}
      <div className="rounded-xl border border-default bg-card/60 overflow-hidden h-[500px]">
        <TagesKarteWidget />
      </div>
    </div>
  );
}
